import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';

import {map} from 'rxjs/operators';
import {forkJoin, Observable} from 'rxjs';
import {of} from 'rxjs/internal/observable/of';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    IndividualSourceOfIncomesService,
    IndividualSourceOfIncomeInterface,
    BusinessModel,
    EmploymentStatusModel,
    IndividualExtendedModel,
    IndividualSourceOfIncomeModel,
    IndividualSourceOfIncomeInputModel
} from '@spa/api';

import {DuplicatedValueValidatorKey, InArrayValidator} from '@spa/validators';

import {BaseControl, Helper, SelectControl} from '../../../../shared/classes';

import {
    SourceOfIncomeControls,
    SourceOfIncomeIdSelector,
    SourceOfIncomeEmploymentStatusSelector,
    SourceOfIncomeBusinessSelector,
    SourceOfIncomesFormArraySelector
} from '../../../../../assets/descriptors';

export class SourceOfIncomesForm {
    private readonly individual: IndividualExtendedModel;
    private readonly employmentStatusesList: EmploymentStatusModel[];
    private readonly businessesList: BusinessModel[];
    private readonly individualSourceOfIncomesService: IndividualSourceOfIncomesService;

    public readonly sourceOfIncomesFormArraySelector: string;

    public form: FormGroup;
    public controls: Array<BaseControl[]>;

    constructor(
        individual: IndividualExtendedModel,
        employmentStatusesList: EmploymentStatusModel[],
        businessList: BusinessModel[],
        individualSourceOfIncomesService: IndividualSourceOfIncomesService
    ) {
        this.individual = individual;
        this.employmentStatusesList = employmentStatusesList;
        this.businessesList = businessList;
        this.individualSourceOfIncomesService = individualSourceOfIncomesService;

        this.sourceOfIncomesFormArraySelector = SourceOfIncomesFormArraySelector;
    }

    private initControls(model?: IndividualSourceOfIncomeModel): BaseControl[] {
        const controls = SourceOfIncomeControls();

        return controls.map((control: BaseControl) => {
            if (model) {
                control.value = this.getSourceOfIncomeProperty(control.name, model);
            }

            if (control.name === SourceOfIncomeEmploymentStatusSelector) {
                const employmentStatusIds =
                    this.employmentStatusesList.map((listItem: EmploymentStatusModel) => listItem.id);

                control.extendValidators(InArrayValidator(employmentStatusIds));

                (control as SelectControl).options =
                    this.employmentStatusesList.map((listItem: EmploymentStatusModel) => {
                        return {
                            label: listItem.name,
                            value: listItem.id
                        };
                    });
            }

            if (control.name === SourceOfIncomeBusinessSelector) {
                const businessIds = this.businessesList.map((listItem: BusinessModel) => listItem.id);

                control.extendValidators(InArrayValidator(businessIds));

                if (model) {
                    const filteredStatuses = this.employmentStatusesList
                        .filter((listItem: EmploymentStatusModel) => model.employmentStatusId === listItem.id);

                    if (filteredStatuses[0] && filteredStatuses[0].businessRequired) {
                        control.extendValidators(Validators.required);
                    }
                }

                (control as SelectControl).options =
                    this.businessesList.map((listItem: BusinessModel) => {
                        return {
                            label: listItem.name,
                            value: listItem.id
                        };
                    });
            }

            return control;
        });
    }


    private getSourceOfIncomeProperty(selector: string, model: IndividualSourceOfIncomeModel): string | number {
        switch (selector) {
            case SourceOfIncomeIdSelector:
                return model.id;
            case SourceOfIncomeEmploymentStatusSelector:
                return model.employmentStatusId;
            case SourceOfIncomeBusinessSelector:
                return model.businessId;
            default:
                return null;
        }
    }

    private getFormControlStatuses(
        formGroup: FormGroup,
        formGroupIndex: number,
        formGroupEmploymentStatusIds: number[],
        messageFunction: (controlLabel?: string) => { [key: string]: string; }
    ): boolean[] {
        return Object.keys(formGroup.controls).map((controlKey: string, controlIndex: number) => {
            const formControl = formGroup.controls[controlKey];
            const control = this.controls[formGroupIndex][controlIndex];

            if (formControl.invalid) {
                const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                    return messageFunction(control.placeholder)[errorKey];
                });

                control.setErrors(errors);

                return false;
            }

            if (control.name === SourceOfIncomeEmploymentStatusSelector) {
                const matchedIndex = formGroupEmploymentStatusIds.indexOf(control.value as number);

                if ((matchedIndex !== formGroupIndex) && control.value) {
                    control.setError(messageFunction(control.placeholder)[DuplicatedValueValidatorKey]);

                    return false;
                }
            }

            return true;
        });
    }

    private removeEmptyGroups(emptyGroupIndexes: number[], formArray: FormArray): void {
        emptyGroupIndexes.sort((previousIndex: number, nextIndex: number) => {
            return nextIndex - previousIndex;
        }).map((index: number) => {
            formArray.removeAt(index);

            this.controls.splice(index, 1);
        });
    }

    private createUpdateRequests(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>>[] {
        return formArray.controls
            .filter((formGroup: FormGroup) => {
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === SourceOfIncomeIdSelector) &&
                        formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .filter(this.removeUnchangedSourceOfIncomes.bind(this))
            .map((formGroup: FormGroup) => {
                const filteredSourceOfIncomes =
                    this.individual.sourceOfIncomes.filter((sourceOfIncome: IndividualSourceOfIncomeModel) => {
                        return sourceOfIncome.id === formGroup.controls[SourceOfIncomeIdSelector].value;
                    });

                const clonedModel = filteredSourceOfIncomes[0].serialize();

                filteredSourceOfIncomes[0].employmentStatusId =
                    formGroup.controls[SourceOfIncomeEmploymentStatusSelector].value;

                filteredSourceOfIncomes[0].businessId =
                    formGroup.controls[SourceOfIncomeBusinessSelector].value;

                return this.individualSourceOfIncomesService.update(filteredSourceOfIncomes[0])
                    .pipe(
                        map(this.updateRequestMap.bind(this, clonedModel))
                    );
            });
    }

    private removeUnchangedSourceOfIncomes(formGroup: FormGroup): boolean {
        const filteredSourceOfIncomes =
            this.individual.sourceOfIncomes.filter((sourceOfIncome: IndividualSourceOfIncomeModel) => {
                return sourceOfIncome.id === formGroup.controls[SourceOfIncomeIdSelector].value;
            });

        if (filteredSourceOfIncomes.length === 0) {
            return false;
        }

        const valuesCheck = Object.keys(formGroup.controls).map((formControlKey: string) => {
            const formControlValue = formGroup.controls[formControlKey].value;

            return this.checkSourceOfIncomeProperty(
                formControlKey,
                formControlValue,
                filteredSourceOfIncomes[0]
            );
        });

        return valuesCheck.indexOf(false) >= 0;
    }

    private checkSourceOfIncomeProperty(
        selector: string,
        formPropertyValue: number | string,
        model: IndividualSourceOfIncomeModel
    ): boolean {
        switch (selector) {
            case SourceOfIncomeIdSelector:
                return model.id === formPropertyValue;
            case SourceOfIncomeEmploymentStatusSelector:
                return model.employmentStatusId === formPropertyValue;
            case SourceOfIncomeBusinessSelector:
                return model.businessId === formPropertyValue;
            default:
                return false;
        }
    }

    private updateRequestMap(
        clonedModel: IndividualSourceOfIncomeInterface,
        response: ApiResponse<ApiError[] | null>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const model = new IndividualSourceOfIncomeModel(clonedModel);

            this.individual.sourceOfIncomes = this.individual.sourceOfIncomes
                .map((sourceOfIncome: IndividualSourceOfIncomeModel) => {
                    if (sourceOfIncome.id === model.id) {
                        sourceOfIncome = model;
                    }

                    return sourceOfIncome;
                });

            return Helper.errorsHelper().createErrorResponse<null>(response);
        }

        return Helper.createSuccessResponse<boolean>(true);
    }

    private createRegistrationRequests(formArray: FormArray): Observable<ApiResponse<ApiError[] | boolean>>[] {
        return formArray.controls
            .filter((formGroup: FormGroup) => {
                return Object.keys(formGroup.controls).filter((formControlKey: string) => {
                    return (formControlKey === SourceOfIncomeIdSelector) &&
                        !formGroup.controls[formControlKey].value;
                }).length !== 0;
            })
            .map(this.createRegistrationRequest.bind(this));
    }

    private createRegistrationRequest(formGroup: FormGroup): Observable<ApiResponse<ApiError[] | boolean>> {
        const inputModel = new IndividualSourceOfIncomeInputModel(
            formGroup.value[SourceOfIncomeEmploymentStatusSelector],
            formGroup.value[SourceOfIncomeBusinessSelector],
            this.individual.id
        );

        return this.individualSourceOfIncomesService.register(inputModel)
            .pipe(
                map(this.registrationRequestMap.bind(this))
            );
    }

    private registrationRequestMap(
        response: ApiResponse<ApiError[] | IndividualSourceOfIncomeInterface>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<IndividualSourceOfIncomeInterface>(response);
        }

        const data = response.data as IndividualSourceOfIncomeInterface;
        const model = new IndividualSourceOfIncomeModel(data);

        this.individual.sourceOfIncomes.push(model);

        return Helper.createSuccessResponse<boolean>(true);
    }

    initForm(): Observable<boolean> {
        this.form = new FormGroup({});
        this.controls = [];

        if (this.individual && this.individual.sourceOfIncomes.length !== 0) {
            this.controls = this.individual.sourceOfIncomes.map((sourceOfIncome: IndividualSourceOfIncomeModel) => {
                return this.initControls(sourceOfIncome);
            });
        } else {
            const controls = this.initControls();

            this.controls.push(controls);
        }

        const formArray = new FormArray([]);

        this.controls.map((controls: BaseControl[]) => {
            const formGroup = new FormGroup({});

            controls.map((control: BaseControl) => {
                formGroup.addControl(control.name, new FormControl(control.value, control.validators));
            });

            formArray.push(formGroup);
        });

        this.form.addControl(this.sourceOfIncomesFormArraySelector, formArray);

        return of(true);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const emptyFormGroupIndexes = [];
        const formArray = (this.form.controls[this.sourceOfIncomesFormArraySelector] as FormArray);

        const formGroupEmploymentStatusIds = formArray.controls.map((formGroup: FormGroup) => {
            return Object.keys(formGroup.controls)
                .filter((controlKey: string) => controlKey === SourceOfIncomeEmploymentStatusSelector)
                .map((controlKey: string) => formGroup.controls[controlKey].value);
        });

        const statuses = formArray.controls.map((formGroup: FormGroup, formGroupIndex: number) => {
            const emptyGroupCheck = Object.keys(formGroup.controls)
                .filter((controlKey: string) => controlKey !== SourceOfIncomeIdSelector)
                .map((controlKey: string) => formGroup.controls[controlKey].value === '');

            if ((emptyGroupCheck.indexOf(false) < 0) && (formGroupIndex > 0)) {
                emptyFormGroupIndexes.push(formGroupIndex);

                return true;
            }

            const formControlStatuses = this.getFormControlStatuses(
                formGroup,
                formGroupIndex,
                [].concat(...formGroupEmploymentStatusIds),
                messageFunction
            );

            return formControlStatuses.indexOf(false) < 0;
        });

        if (emptyFormGroupIndexes.length !== 0) {
            this.removeEmptyGroups(emptyFormGroupIndexes, formArray);
        }

        return statuses.indexOf(false) < 0;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> | null {
        if (this.form.dirty) {
            const requests = [];

            const formArray = this.form.controls[this.sourceOfIncomesFormArraySelector] as FormArray;

            if (this.individual && this.individual.sourceOfIncomes.length !== 0) {
                const updateRequests = this.createUpdateRequests(formArray);

                if (updateRequests.length !== 0) {
                    requests.push(...updateRequests);
                }
            }

            const registrationRequests = this.createRegistrationRequests(formArray);

            if (registrationRequests.length !== 0) {
                requests.push(...registrationRequests);
            }

            if (requests.length !== 0) {
                return forkJoin(requests)
                    .pipe(
                        map((responses: ApiResponse<ApiError[] | boolean>[]) => {
                            if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
                                return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
                            }

                            return Helper.createSuccessResponse<boolean>(true);
                        })
                    );
            }
        }

        return null;
    }
}
